import { SequencingTypeEnum, TableTypeEnum, TrackEnum } from "@selector/shared/openapi";
import { useEffect, useMemo, useState } from "react";
import { Item, MenuOptionItem, Select } from "widgets";
import { useShallow } from "zustand/shallow";

import { useGenomeViewerStore } from "../../store/store.ts";
import type { SubType } from "../../types.ts";
import { getTableTypeFromTrackType } from "../../utils/type-conversion.ts";
import { TableTypeNamesMap } from "./settings.ts";

export interface TableTypeItem {
  type: TableTypeEnum;
  subType?: SubType;
}

interface TableTypeSelectProps {
  tableType: TableTypeItem;
  onChange: (tableTypeItem: TableTypeItem) => void;
}

export const TableTypeSelect = ({ tableType, onChange }: TableTypeSelectProps) => {
  const syntenyType = useGenomeViewerStore((state) => state.syntenyOrganism.type);
  const tracks = useGenomeViewerStore(useShallow((state) => state.tracks));
  const defaultTableTypes: TableTypeItem[] = useMemo(
    () =>
      syntenyType === SequencingTypeEnum.PANGENOME
        ? [{ type: TableTypeEnum.PANGENOME }, { type: TableTypeEnum.ORGANISM }]
        : [{ type: TableTypeEnum.ORGANISM }],
    [syntenyType],
  );

  const [trackTableTypes, setTrackTableTypes] = useState<TableTypeItem[]>(defaultTableTypes);

  useEffect(() => {
    const trackItems: TableTypeItem[] = [...tracks.values()]
      .filter((track) => track.trackType !== TrackEnum.SEQUENCE)
      .map((track) => ({
        type: getTableTypeFromTrackType(track.trackType),
        subType: track.subType,
      }));

    const uniqueTableTypes: TableTypeItem[] = [];

    for (const trackItem of trackItems) {
      if (
        !uniqueTableTypes.some((track) => track.type === trackItem.type && track.subType?.id === trackItem.subType?.id)
      ) {
        uniqueTableTypes.push(trackItem);
      }
    }
    const availableTableTracks = defaultTableTypes.concat(uniqueTableTypes);

    if (
      !availableTableTracks.some(
        (track) => track.type === tableType.type && track.subType?.id === tableType.subType?.id,
      )
    ) {
      onChange(availableTableTracks[0]);
    }

    setTrackTableTypes(availableTableTracks);
  }, [tracks, syntenyType, onChange, tableType, defaultTableTypes]);

  return (
    <Select
      selectedKey={String(
        trackTableTypes.findIndex((t) => t.type === tableType.type && t.subType?.id === tableType.subType?.id) ?? 0,
      )}
      onSelectionChange={(key) => {
        const numKey = Number(key);
        const selectedTableType = trackTableTypes[numKey];

        if (!selectedTableType) return;
        onChange(selectedTableType);
      }}
      width={180}
      items={trackTableTypes}
    >
      {(item) => {
        const tableTypeName = item.subType ? item.subType.name : TableTypeNamesMap[item.type];
        const tableTypeDescription = item.subType ? TableTypeNamesMap[TableTypeEnum.NU_FEATURE] : undefined;
        return (
          <Item
            key={String(
              trackTableTypes.findIndex((t) => t.type === item.type && t.subType?.id === item.subType?.id) ?? 0,
            )}
            textValue={tableTypeName}
          >
            <MenuOptionItem title={tableTypeName} description={tableTypeDescription} />
          </Item>
        );
      }}
    </Select>
  );
};
